import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Calendar, Users, Heart, Music, Sparkles, Building2, PartyPopper, Home as HomeIcon, MessageCircle, Gift, Cake, Star } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';
const CELEBRATIONS_URL = `https://wa.me/529983032313?text=${encodeURIComponent('Hola, me gustaría organizar una celebración con ABBA Massage. ¿Podemos platicar de las opciones?')}`;

const IMAGES = {
  events: 'https://customer-assets.emergentagent.com/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/c3wz2s4x_ChatGPT%20Image%208%20mar%202026%2C%2012_18_30%20a.m..png',
  soundHealing: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/vkqrk8xy_singing%20bowls.png',
  reiki: 'https://customer-assets.emergentagent.com/job_5bf56e5f-42e0-4cfb-a653-edc31a7a571c/artifacts/jbr2lcfd_Miri%20Reiki.JPG',
  team: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/15z1ydle_TERAPEUTAS%20ABBA.JPG',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export const Events = () => {
  const { language } = useLanguage();

  const eventServices = [
    {
      icon: Heart,
      name: language === 'es' ? 'Masaje Shiatsu en Silla' : 'Shiatsu Chair Massage',
      desc: language === 'es' ? 'Masaje rápido y efectivo perfecto para eventos corporativos y reuniones.' : 'Quick and effective massage perfect for corporate events and gatherings.'
    },
    {
      icon: Sparkles,
      name: 'Reiki',
      desc: language === 'es' ? 'Sesiones de sanación energética para grupos.' : 'Energy healing sessions for groups.'
    },
    {
      icon: Users,
      name: language === 'es' ? 'Meditación Guiada' : 'Guided Meditation',
      desc: language === 'es' ? 'Sesiones de meditación para relajación grupal.' : 'Meditation sessions for group relaxation.'
    },
    {
      icon: Music,
      name: language === 'es' ? 'Experiencias de Sanación con Sonido' : 'Sound Healing Experiences',
      desc: language === 'es' ? 'Baños de sonido con cuencos tibetanos.' : 'Sound baths with Tibetan singing bowls.'
    },
    {
      icon: Heart,
      name: language === 'es' ? 'Masaje de Relajación para Eventos' : 'Relaxation Massage for Events',
      desc: language === 'es' ? 'Masajes relajantes para invitados especiales.' : 'Relaxing massages for special guests.'
    },
    {
      icon: Calendar,
      name: language === 'es' ? 'Experiencias de Bienestar para Grupos' : 'Wellness Experiences for Groups',
      desc: language === 'es' ? 'Experiencias personalizadas de bienestar grupal.' : 'Customized group wellness experiences.'
    },
  ];

  const eventTypes = [
    { icon: Building2, name: language === 'es' ? 'Eventos Corporativos de Bienestar' : 'Corporate Wellness Events' },
    { icon: Users, name: language === 'es' ? 'Retiros' : 'Retreats' },
    { icon: Heart, name: language === 'es' ? 'Bodas' : 'Weddings' },
    { icon: PartyPopper, name: language === 'es' ? 'Celebraciones Privadas' : 'Private Celebrations' },
    { icon: HomeIcon, name: language === 'es' ? 'Experiencias Grupales en Villas o Rentas Vacacionales' : 'Group Experiences in Vacation Rentals or Villas' },
  ];

  const celebrationPhotos = [
    { src: '/images/celebration-wellness-toast.jpg', caption: language === 'es' ? 'Un brindis para celebrar' : 'A toast to celebrate', alt: language === 'es' ? 'Amigas brindando después de una experiencia de bienestar' : 'Friends sharing a toast after a wellness experience' },
    { src: '/images/celebration-birthday-details.jpg', caption: language === 'es' ? 'Los detalles de cumpleaños' : 'Birthday details', alt: language === 'es' ? 'Decoración de cumpleaños con pastelitos y té' : 'Birthday decorations with small cakes and tea' },
    { src: '/images/celebration-in-home-seaside.jpg', caption: language === 'es' ? 'Celebrar en tu espacio' : 'Celebrate at your location', alt: language === 'es' ? 'Camillas de masaje y decoración de cumpleaños en una residencia frente al mar' : 'Massage tables and birthday decorations at a seaside residence' },
    { src: '/images/celebration-couple-birthday.jpg', caption: language === 'es' ? 'Un cumpleaños en pareja' : 'A birthday together', alt: language === 'es' ? 'Una pareja soplando las velas de sus pastelitos' : 'A couple blowing out candles on their small cakes' },
    { src: '/images/celebration-friends-studio.jpg', caption: language === 'es' ? 'Compartir en el estudio' : 'Together at the studio', alt: language === 'es' ? 'Amigas en batas rosas brindando en el estudio' : 'Friends in pink robes sharing a toast at the studio' }
  ];

  const celebrations = [
    {
      icon: Cake,
      title: language === 'es' ? 'Cumpleaños' : 'Birthdays',
      desc: language === 'es' ? 'Un momento para consentir a quien cumple años y disfrutar juntos, en el estudio o en tu espacio.' : 'A moment to care for the person celebrating and enjoy time together, at our studio or your location.'
    },
    {
      icon: PartyPopper,
      title: language === 'es' ? 'Despedidas de soltera' : 'Bachelorette celebrations',
      desc: language === 'es' ? 'Una pausa entre amigas antes del gran día, con masajes y experiencias de bienestar para el grupo.' : 'A moment to slow down together before the big day, with massage and wellness experiences for the group.'
    },
    {
      icon: Star,
      title: language === 'es' ? 'Cumpleaños de niñas' : 'Girls’ birthdays',
      desc: language === 'es' ? 'Una celebración de bienestar pensada para su edad y organizada contigo. Escríbenos para conocer las opciones.' : 'An age-appropriate wellness celebration planned with you. Ask us about the available options.'
    },
    {
      icon: Gift,
      title: language === 'es' ? 'Navidad y fin de año' : 'Christmas & year-end',
      desc: language === 'es' ? 'Un espacio para compartir, descansar y cerrar el año con las personas que quieres.' : 'Time to connect, rest, and close the year with the people you care about.'
    },
  ];

  return (
    <div data-testid="events-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 min-h-[60vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.events}
            alt="Events & Experiences"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {language === 'es' ? 'Bienestar para tus ocasiones especiales' : 'Wellness for your special occasions'}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {language === 'es' ? 'Eventos y Experiencias' : 'Events & Experiences'}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/80 mb-8 font-['Manrope']">
              {language === 'es' 
                ? 'ABBA Massage crea experiencias de bienestar para cumpleaños, despedidas de soltera, Navidad, bodas, retiros y grupos.'
                : 'ABBA Massage creates wellness experiences for birthdays, bachelorette celebrations, Christmas, weddings, retreats, and groups.'}
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  {language === 'es' ? 'Contáctanos' : 'Contact Us'}
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services for Events */}
      <section className="py-24 md:py-32 px-6 bg-white" data-testid="events-services">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-4 font-['Manrope'] font-semibold">
              {language === 'es' ? 'Nuestros Servicios' : 'Our Services'}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] tracking-tight">
              {language === 'es' ? 'Servicios para Eventos' : 'Services for Events'}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {eventServices.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-[#F8F6F3] rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#A91B60]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C] mb-3">
                  {service.name}
                </h3>
                <p className="text-[#5A5A5A] font-['Manrope']">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-24 md:py-32 px-6 bg-[#F8F6F3]" data-testid="event-types">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={IMAGES.team}
                alt="ABBA Team at Events"
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-8 tracking-tight">
                {language === 'es' 
                  ? <>Perfecto para <em className="italic text-[#A91B60]">cualquier ocasión</em></>
                  : <>Perfect for <em className="italic text-[#A91B60]">any occasion</em></>}
              </motion.h2>
              
              <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] mb-8 font-['Manrope']">
                {language === 'es'
                  ? 'Nuestros servicios pueden adaptarse para diferentes tipos de eventos y grupos.'
                  : 'Our services can be adapted for different types of events and groups.'}
              </motion.p>

              <motion.div variants={fadeInUp} className="space-y-4">
                {eventTypes.map((type, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-[#A91B60]/10 flex items-center justify-center flex-shrink-0">
                      <type.icon className="w-5 h-5 text-[#A91B60]" />
                    </div>
                    <span className="text-[#1C1C1C] font-['Manrope'] font-medium">
                      {type.name}
                    </span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Celebrations: a permanent section for private occasions */}
      <section id="celebrations" className="py-24 md:py-32 px-6 bg-white scroll-mt-24" data-testid="celebrations-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <p className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-4 font-['Manrope'] font-semibold">
              {language === 'es' ? 'Celebraciones con intención' : 'Celebrate with intention'}
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6">
              {language === 'es' ? '¿Por qué celebrar con ABBA?' : 'Why celebrate with ABBA?'}
            </h2>
            <p className="text-lg text-[#5A5A5A] font-['Manrope'] leading-relaxed">
              {language === 'es'
                ? 'Porque celebrar también puede ser hacer una pausa y disfrutar de verdad a las personas que tienes cerca. Diseñamos cada experiencia contigo: masajes y momentos de bienestar que se adaptan a la ocasión, el grupo y el lugar.'
                : 'Celebrating can also mean slowing down and truly enjoying the people around you. We plan each experience with you, adapting massage and wellness moments to the occasion, your group, and your space.'}
            </p>
          </div>

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 mb-12" data-testid="celebrations-gallery">
            {celebrationPhotos.map((photo) => (
              <figure key={photo.src} className="break-inside-avoid mb-6 overflow-hidden rounded-2xl bg-[#F8F6F3]">
                <a href={photo.src} target="_blank" rel="noopener noreferrer" aria-label={photo.alt}>
                  <img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" className="w-full h-auto" />
                </a>
                <figcaption className="px-5 py-4 text-[#5A5A5A] font-['Manrope']">{photo.caption}</figcaption>
              </figure>
            ))}
          </div>

          <div className="mb-12 max-w-3xl mx-auto" data-testid="celebrations-video">
            <video
              controls
              playsInline
              preload="metadata"
              className="w-full rounded-2xl bg-black"
              aria-label={language === 'es' ? 'No siempre hacen falta flores: celebraciones con ABBA' : 'Flowers are not always needed: celebrations with ABBA'}
            >
              <source src="/celebraciones-abba-web%20(3).mp4" type="video/mp4" />
              {language === 'es' ? 'Tu navegador no puede reproducir este video.' : 'Your browser cannot play this video.'}
            </video>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {celebrations.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-[#F8F6F3] rounded-2xl p-7">
                <Icon className="w-8 h-8 text-[#A91B60] mb-5" aria-hidden="true" />
                <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C] mb-3">{title}</h3>
                <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#A91B60]/10 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row gap-8 md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h3 className="font-['Playfair_Display'] text-2xl text-[#1C1C1C] mb-3">
                {language === 'es' ? 'Los detalles hacen la diferencia' : 'It’s the little details'}
              </h3>
              <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">
                {language === 'es'
                  ? 'Podemos coordinar detalles como decoración, un pastelito o una sorpresa para la persona festejada. Cuéntanos qué celebras y te diremos qué es posible para tu fecha y grupo.'
                  : 'We can coordinate details such as decorations, a small cake, or a surprise for the guest of honor. Tell us what you are celebrating and we’ll confirm what is possible for your date and group.'}
              </p>
            </div>
            <a href={CELEBRATIONS_URL} target="_blank" rel="noopener noreferrer" className="shrink-0">
              <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                <MessageCircle className="mr-2 w-5 h-5" />
                {language === 'es' ? 'Planea tu celebración' : 'Plan your celebration'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Sound Healing Image */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={IMAGES.soundHealing}
              alt="Sound Healing Experience"
              className="w-full rounded-2xl shadow-xl aspect-[21/9] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#A91B60]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-6 tracking-tight">
              {language === 'es' 
                ? <>Planifica tu <em className="italic text-white/80">evento de bienestar</em></>
                : <>Plan your <em className="italic text-white/80">wellness event</em></>}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-10 font-['Manrope']">
              {language === 'es'
                ? 'Contáctanos para crear una experiencia de bienestar personalizada para tu grupo.'
                : 'Contact us to create a custom wellness experience for your group.'}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-white hover:bg-gray-100 text-[#A91B60] rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  {language === 'es' ? 'Contáctanos' : 'Contact Us'}
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Events;
